import { useState, Fragment } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  useListWorkflowRunsForDefinition,
  useGetWorkflowRun,
  useGetWorkflowDefinition,
} from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import {
  CaretDownIcon,
  CaretRightIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { WorkflowRunResponse } from '@erp/rpc';
import { WorkflowRunStepsList } from './components/WorkflowRunStepsList';

function formatDuration(
  startedAt?: string,
  completedAt?: string,
): string | null {
  if (!startedAt || !completedAt) {
    return null;
  }
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;
  const seconds = durationMs / 1000;
  return seconds.toFixed(3);
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'completed':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'running':
    case 'queued':
      return 'secondary';
    case 'cancelled':
      return 'outline';
    default:
      return 'outline';
  }
}

interface WorkflowRunRowProps {
  run: WorkflowRunResponse;
  stepNames: Map<string, string>;
  stepTypes: Map<string, string>;
}

function WorkflowRunRow({ run, stepNames, stepTypes }: WorkflowRunRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: runDetail, refetch: refetchRun } = useGetWorkflowRun(
    isExpanded ? run.id : undefined,
  );

  return (
    <Fragment>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <TableCell className="w-8">
          <Button
            variant="ghost"
            size="sm"
            className="p-0 h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <CaretDownIcon className="h-4 w-4" />
            ) : (
              <CaretRightIcon className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
        <TableCell>
          <Badge variant={getStatusVariant(run.status)}>{run.status}</Badge>
        </TableCell>
        <TableCell>
          <div className="flex flex-col">
            <span className="text-sm">
              {formatDistanceToNow(new Date(run.triggeredAt), {
                addSuffix: true,
              })}
            </span>
            <span className="text-xs text-muted-foreground">
              {new Date(run.triggeredAt).toLocaleString()}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-right font-mono">
          {formatDuration(run.startedAt, run.completedAt) || '-'}
        </TableCell>
        <TableCell>
          {run.completedSteps} / {run.totalSteps}
        </TableCell>
        <TableCell>
          {run.stopReason ? (
            <span className="text-sm text-destructive line-clamp-2">
              {run.stopReason}
            </span>
          ) : (
            '-'
          )}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={6} className="p-0 bg-muted/30">
            <div className="p-4 border-t">
              <h4 className="text-sm font-medium mb-3">Step Runs</h4>
              {runDetail ? (
                <WorkflowRunStepsList
                  workflowRunId={run.id}
                  stepNames={stepNames}
                  stepTypes={stepTypes}
                  onRefresh={() => refetchRun()}
                />
              ) : (
                <div className="flex items-center justify-center py-4">
                  <CircleNotchIcon className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

export function WorkflowDefinitionRunsTab() {
  const { workflowDefinitionId } = useParams({
    from: '/authenticated/workflows/$workflowDefinitionId',
  });
  const { data: runsData, isLoading: isLoadingRuns } =
    useListWorkflowRunsForDefinition(workflowDefinitionId);
  const { data: definitionData, isLoading: isLoadingDefinition } =
    useGetWorkflowDefinition(workflowDefinitionId);

  const stepNames = new Map<string, string>();
  const stepTypes = new Map<string, string>();
  if (definitionData?.steps) {
    for (const step of definitionData.steps) {
      stepNames.set(step.id, step.name);
      stepTypes.set(step.id, step.type);
    }
  }

  const isLoading = isLoadingRuns || isLoadingDefinition;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const runs = runsData?.runs || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Runs ({runsData?.total || 0})</CardTitle>
      </CardHeader>
      <CardContent>
        {runs.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No runs found for this workflow definition.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Triggered At</TableHead>
                <TableHead className="text-right">Duration (sec)</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Stop Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <WorkflowRunRow
                  key={run.id}
                  run={run}
                  stepNames={stepNames}
                  stepTypes={stepTypes}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
